import React, { useState } from 'react';
import { CheckCircle2, Download, Trash2, ExternalLink, Facebook, Linkedin, Edit2, MessageSquare, ChevronDown, ChevronUp, AlertCircle, FileText, Search, User, Send, Calendar } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { WonkaPeopleTab } from './WonkaPeopleTab';
import { WonkaFinancialsTab } from './WonkaFinancialsTab';
import { WonkaDetailsTab } from './WonkaDetailsTab';
import { WonkaNotificationsTab } from './WonkaNotificationsTab';
import { WonkaOverviewTab } from './WonkaOverviewTab';
import { WonkaApplicationView } from './WonkaApplicationView';
import { WonkaResearchAgentsTab } from './WonkaResearchAgentsTab';
import { WonkaAuditLogTab } from './WonkaAuditLogTab';
import { NotesModal } from './NotesModal';
import { ScoutAIButton } from './ScoutAIButton';
const wonkaNotes = [{
  id: '1',
  type: 'user' as const,
  author: 'Sarah Chen',
  timestamp: 'February 20, 2026',
  title: 'Excellent Payment History',
  content: 'Wonka has consistently paid within terms for the past 12 months. Strong financials and growing market share. Recommend increasing credit limit to $150K if requested.'
}, {
  id: '2',
  type: 'system' as const,
  timestamp: 'January 15, 2026 · 3:45 PM',
  title: 'Credit Approved',
  content: 'Credit limit set to $100,000 with Net 30 terms. Score at approval: 86 (Excellent Standing).'
}, {
  id: '3',
  type: 'system' as const,
  timestamp: 'January 12, 2026 · 11:20 AM',
  title: 'Application Submitted',
  content: 'Submitted by Charlie Bucket on behalf of Wonka Industries.'
}, {
  id: '4',
  type: 'system' as const,
  timestamp: 'January 10, 2026 · 9:30 AM',
  title: 'Application Started',
  content: 'Credit application initiated for Wonka Industries.'
}];
export function WonkaCreditApplication() {
  const [activeTab, setActiveTab] = useState('OVERVIEW');
  const [decisionOpen, setDecisionOpen] = useState(false);
  const [activeModal, setActiveModal] = useState<null | 'approve' | 'approveWithChanges' | 'reject'>(null);
  const [decisionMade, setDecisionMade] = useState<null | 'approved' | 'approvedWithChanges' | 'rejected'>(null);
  const [expandedRef, setExpandedRef] = useState<string | null>(null);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const handleDecision = (decision: 'approved' | 'approvedWithChanges' | 'rejected') => {
    setDecisionMade(decision);
    setActiveModal(null);
  };
  return <div className="min-h-full bg-gray-50 p-6" data-id="element-3377">
      <div className="max-w-7xl mx-auto space-y-6" data-id="element-3378">
        {/* Header Card */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4" data-id="element-3379">
          <div className="flex items-center justify-between gap-4" data-id="element-3380">
            {/* Left: Company Info + Notes */}
            <div className="shrink-0" data-id="element-3381">
              <h1 className="text-lg font-semibold text-gray-900 mb-1" data-id="element-3382">
                WONKA INDUSTRIES
              </h1>
              <div className="flex items-center gap-3 text-indigo-600 mb-2" data-id="element-3383">
                <ExternalLink className="w-3.5 h-3.5 cursor-pointer" data-id="element-3384" />
                <Facebook className="w-3.5 h-3.5 cursor-pointer" data-id="element-3385" />
                <Linkedin className="w-3.5 h-3.5 cursor-pointer" data-id="element-3386" />
                <Edit2 className="w-3.5 h-3.5 cursor-pointer" data-id="element-3387" />
              </div>
              <div className="space-y-0.5 text-xs text-gray-600 mb-2" data-id="element-3388">
                <div className="flex items-center gap-1.5" data-id="element-3389">
                  <span className="font-semibold text-gray-900" data-id="element-3390">
                    Credit Manager:
                  </span>
                  <span data-id="element-3391">Sarah Chen</span>
                  <Edit2 className="w-3 h-3 text-indigo-600 cursor-pointer" data-id="element-3392" />
                </div>
                <div className="flex items-center gap-1.5" data-id="element-3393">
                  <span className="font-semibold text-gray-900" data-id="element-3394">Added:</span>
                  <span data-id="element-3395">1/10/2026</span>
                </div>
              </div>
              <button onClick={() => setShowNotesModal(true)} className="bg-indigo-600 hover:bg-indigo-700 text-white px-2.5 py-1 rounded flex items-center gap-1.5 text-xs font-medium relative" data-id="element-3396">
                <MessageSquare className="w-3 h-3" data-id="element-3397" />
                NOTES
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center" data-id="element-3398">
                  {wonkaNotes.length}
                </span>
              </button>
            </div>

            {/* Right: Risk Gauge */}
            <div className="flex flex-col items-center shrink-0 mr-4" data-id="element-3399">
              <div className="flex items-end gap-2 mb-1" data-id="element-3400">
                <span className="text-xs text-gray-400 font-medium" data-id="element-3401">0</span>
                <div className="relative w-64" data-id="element-3402">
                  {/* Score label */}
                  <div className="absolute -top-6 left-[88%] -translate-x-1/2 bg-green-100 text-green-800 text-xs font-bold px-2 py-0.5 rounded" data-id="element-3403">
                    88
                  </div>
                  {/* Bar */}
                  <div className="relative w-full h-6 flex rounded-full overflow-hidden" data-id="element-3404">
                    <div className="w-1/3 bg-red-300" data-id="element-3405"></div>
                    <div className="w-1/3 bg-yellow-300" data-id="element-3406"></div>
                    <div className="w-1/3 bg-green-300" data-id="element-3407"></div>
                  </div>
                  {/* Indicator line */}
                  <div className="absolute top-0 bottom-0 left-[88%] w-0.5 flex flex-col items-center" data-id="element-3408">
                    <div className="w-[3px] h-full bg-gray-700 rounded-full" style={{
                    borderLeft: '2px dashed #374151'
                  }} data-id="element-3409"></div>
                  </div>
                </div>
                <span className="text-xs text-gray-400 font-medium" data-id="element-3410">100</span>
              </div>
              <div className="w-64 flex justify-between px-0 mt-1" data-id="element-3411">
                <span className="text-xs text-red-500 font-medium pl-7" data-id="element-3412">
                  Low
                </span>
                <span className="text-xs text-yellow-600 font-medium" data-id="element-3413">
                  Medium
                </span>
                <span className="text-xs text-gray-600 font-medium pr-7" data-id="element-3414">
                  High
                </span>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex border-t border-gray-200 mt-4 pt-3" data-id="element-3415">
            {['OVERVIEW', 'NOTIFICATIONS', 'DETAILS', 'FINANCIALS', 'PEOPLE', 'RESEARCH AGENTS', 'APPLICATIONS', 'AUDIT LOG'].map(tab => <motion.button key={tab} whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} onClick={() => {
            setActiveTab(tab);
            setDecisionOpen(false);
          }} className={`flex-1 text-center py-1.5 text-xs font-semibold transition-colors ${tab === activeTab ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-3416">
                {tab}
              </motion.button>)}
          </div>
        </div>

        {/* Credit Decision Section */}
        <div className="bg-green-50 border border-green-200 rounded-lg shadow-sm overflow-hidden" data-id="element-3417">
          {/* Header Row */}
          <button onClick={() => setDecisionOpen(!decisionOpen)} className="w-full p-4 flex items-center justify-between bg-green-100 hover:bg-green-150 transition-colors" data-id="element-3418">
            <div className="flex items-center gap-4" data-id="element-3419">
              <span className="font-bold text-gray-900" data-id="element-3420">Credit Decision</span>
              <span className="border border-green-600 text-green-700 px-2.5 py-0.5 rounded-full text-sm font-medium" data-id="element-3421">
                Approved
              </span>
              <span className="text-sm text-gray-500" data-id="element-3422">
                Date: January 10, 2026
              </span>
              <span className="text-sm text-gray-300" data-id="element-3423">·</span>
              <span className="text-sm font-medium text-gray-700" data-id="element-3424">
                Approved Credit Limit: $100,000
              </span>
            </div>
            {decisionOpen ? <ChevronUp className="w-5 h-5 text-gray-400" data-id="element-3425" /> : <ChevronDown className="w-5 h-5 text-gray-400" data-id="element-3426" />}
          </button>

          {/* Expanded Content */}
          {decisionOpen && <div className="p-6 border-t border-green-200" data-id="element-3427">
              <div className="grid grid-cols-2 gap-x-12 gap-y-6 mb-8" data-id="element-3428">
                {/* Left Column */}
                <div className="space-y-4" data-id="element-3429">
                  <div data-id="element-3430">
                    <p className="text-sm text-gray-500 mb-1" data-id="element-3431">
                      Approved Decision
                    </p>
                    <p className="text-xl font-bold text-gray-900" data-id="element-3432">
                      Approved for $100,000
                    </p>
                  </div>
                  <div data-id="element-3433">
                    <p className="text-sm font-medium text-gray-700 mb-1" data-id="element-3434">
                      Terms
                    </p>
                    <div className="text-sm text-gray-600 space-y-0.5" data-id="element-3435">
                      <p data-id="element-3436">Payment Terms Days: 30</p>
                      <p data-id="element-3437">Discount: 2%</p>
                      <p data-id="element-3438">Discount Days: 10</p>
                      <p data-id="element-3439">Late Pay Penalty: 1.5%</p>
                    </div>
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-4" data-id="element-3440">
                  <div data-id="element-3441">
                    <div className="flex items-center gap-2 mb-1" data-id="element-3442">
                      <p className="text-sm text-gray-500" data-id="element-3443">Policy Applied</p>
                      <span className="text-sm text-gray-300" data-id="element-3444">·</span>
                      <button className="text-sm text-indigo-600 hover:text-indigo-700 italic" data-id="element-3445">
                        (View Policy)
                      </button>
                    </div>
                    <p className="font-bold text-gray-900" data-id="element-3446">
                      My Company's Credit Policy v1
                    </p>
                  </div>

                  <div data-id="element-3447">
                    <div className="flex items-center gap-2 mb-1" data-id="element-3448">
                      <p className="text-sm text-gray-500" data-id="element-3449">
                        Credit Application
                      </p>
                      <span className="text-sm text-gray-300" data-id="element-3450">·</span>
                      <button className="text-sm text-indigo-600 hover:text-indigo-700 italic" data-id="element-3451">
                        (View Application)
                      </button>
                    </div>
                    <p className="text-sm text-gray-700" data-id="element-3452">
                      Submitted on: January 10, 2026
                    </p>
                  </div>

                  <div data-id="element-3453">
                    <div className="flex items-center gap-2 mb-1" data-id="element-3454">
                      <p className="text-sm text-gray-500" data-id="element-3455">
                        Recommendation Summary
                      </p>
                      <span className="text-sm text-gray-300" data-id="element-3456">·</span>
                      <button className="text-sm text-indigo-600 hover:text-indigo-700 italic" data-id="element-3457">
                        (Full Reasoning)
                      </button>
                    </div>
                    <p className="text-sm text-gray-700 leading-relaxed" data-id="element-3458">
                      Wonka Industries Ltd. is approved for credit based on
                      strong business fundamentals, an established 52-year
                      operating history in confectionery manufacturing, and
                      positive trade references. The company demonstrates
                      excellent financial health and consistent payment
                      patterns.
                    </p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="grid grid-cols-4 gap-4" data-id="element-3459">
                <button className="bg-gray-300 text-gray-500 py-3 rounded-lg font-semibold text-sm cursor-not-allowed" data-id="element-3460">
                  APPROVE
                </button>
                <button className="bg-gray-300 text-gray-500 py-3 rounded-lg font-semibold text-sm cursor-not-allowed" data-id="element-3461">
                  APPROVE WITH CHANGES
                </button>
                <button className="bg-gray-300 text-gray-500 py-3 rounded-lg font-semibold text-sm cursor-not-allowed" data-id="element-3462">
                  REJECT
                </button>
                <button className="border-2 border-gray-300 text-gray-400 py-3 rounded-lg font-semibold text-sm cursor-not-allowed" data-id="element-3463">
                  REGENERATE RECOMMENDATION
                </button>
              </div>
            </div>}
        </div>

        {/* Decision Modals */}
        <AnimatePresence data-id="element-3464">
          {activeModal && <>
              {/* Backdrop */}
              <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} onClick={() => setActiveModal(null)} className="fixed inset-0 bg-black/50 z-50" data-id="element-3465" />

              {/* Modal */}
              <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.95
          }} className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-3466">
                <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full p-8" data-id="element-3467">
                  {activeModal === 'approve' && <>
                      <h2 className="text-3xl font-bold text-gray-900 mb-8" data-id="element-3468">
                        Approve Recommendation
                      </h2>
                      <div className="space-y-6" data-id="element-3469">
                        <div className="grid grid-cols-3 gap-4" data-id="element-3470">
                          <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3 col-span-2" data-id="element-3471">
                            <legend className="text-sm text-gray-500 px-1" data-id="element-3472">
                              Credit Limit
                            </legend>
                            <input type="text" defaultValue="100,000" className="w-full text-lg text-gray-900 outline-none" data-id="element-3473" />
                          </fieldset>
                          <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3" data-id="element-3474">
                            <legend className="text-sm text-gray-500 px-1" data-id="element-3475">
                              Currency
                            </legend>
                            <select className="w-full text-lg text-gray-900 outline-none bg-transparent" data-id="element-3476">
                              <option data-id="element-3477">USD</option>
                            </select>
                          </fieldset>
                        </div>
                        <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3" data-id="element-3478">
                          <legend className="text-sm text-gray-500 px-1" data-id="element-3479">
                            Payment Terms Days
                          </legend>
                          <input type="text" defaultValue="30" className="w-full text-lg text-gray-900 outline-none" data-id="element-3480" />
                        </fieldset>
                        <div className="grid grid-cols-2 gap-4" data-id="element-3481">
                          <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3" data-id="element-3482">
                            <legend className="text-sm text-gray-500 px-1" data-id="element-3483">
                              Discount (%)
                            </legend>
                            <input type="text" placeholder="" className="w-full text-lg text-gray-900 outline-none" data-id="element-3484" />
                          </fieldset>
                          <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3" data-id="element-3485">
                            <legend className="text-sm text-gray-500 px-1" data-id="element-3486">
                              Discount Days
                            </legend>
                            <input type="text" placeholder="" className="w-full text-lg text-gray-900 outline-none" data-id="element-3487" />
                          </fieldset>
                        </div>
                        <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3" data-id="element-3488">
                          <legend className="text-sm text-gray-500 px-1" data-id="element-3489">
                            Late Pay Penalty (%)
                          </legend>
                          <input type="text" placeholder="" className="w-full text-lg text-gray-900 outline-none" data-id="element-3490" />
                        </fieldset>
                        <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3" data-id="element-3491">
                          <legend className="text-sm text-gray-500 px-1" data-id="element-3492">
                            Reasoning
                          </legend>
                          <textarea rows={4} className="w-full text-lg text-gray-900 outline-none resize-none" data-id="element-3493" />
                        </fieldset>
                      </div>
                      <div className="flex justify-end gap-4 mt-8" data-id="element-3494">
                        <button onClick={() => setActiveModal(null)} className="text-indigo-600 font-semibold px-6 py-3 hover:bg-indigo-50 rounded" data-id="element-3495">
                          CANCEL
                        </button>
                        <button onClick={() => handleDecision('approved')} className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-8 py-3 rounded" data-id="element-3496">
                          APPROVE
                        </button>
                      </div>
                    </>}

                  {activeModal === 'approveWithChanges' && <>
                      <h2 className="text-3xl font-bold text-gray-900 mb-8" data-id="element-3497">
                        Approve and Input Values
                      </h2>
                      <div className="space-y-6" data-id="element-3498">
                        <div className="grid grid-cols-3 gap-4" data-id="element-3499">
                          <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3 col-span-2" data-id="element-3500">
                            <legend className="text-sm text-gray-500 px-1" data-id="element-3501">
                              Credit Limit *
                            </legend>
                            <input type="text" defaultValue="100,000" className="w-full text-lg text-gray-900 outline-none" data-id="element-3502" />
                          </fieldset>
                          <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3" data-id="element-3503">
                            <legend className="text-sm text-gray-500 px-1" data-id="element-3504">
                              Currency
                            </legend>
                            <select className="w-full text-lg text-gray-900 outline-none bg-transparent" data-id="element-3505">
                              <option data-id="element-3506">USD</option>
                            </select>
                          </fieldset>
                        </div>
                        <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3" data-id="element-3507">
                          <legend className="text-sm text-gray-500 px-1" data-id="element-3508">
                            Payment Terms Days
                          </legend>
                          <input type="text" defaultValue="30" className="w-full text-lg text-gray-900 outline-none" data-id="element-3509" />
                        </fieldset>
                        <div className="grid grid-cols-2 gap-4" data-id="element-3510">
                          <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3" data-id="element-3511">
                            <legend className="text-sm text-gray-500 px-1" data-id="element-3512">
                              Discount (%)
                            </legend>
                            <input type="text" placeholder="" className="w-full text-lg text-gray-900 outline-none" data-id="element-3513" />
                          </fieldset>
                          <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3" data-id="element-3514">
                            <legend className="text-sm text-gray-500 px-1" data-id="element-3515">
                              Discount Days
                            </legend>
                            <input type="text" placeholder="" className="w-full text-lg text-gray-900 outline-none" data-id="element-3516" />
                          </fieldset>
                        </div>
                        <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3" data-id="element-3517">
                          <legend className="text-sm text-gray-500 px-1" data-id="element-3518">
                            Late Pay Penalty (%)
                          </legend>
                          <input type="text" placeholder="" className="w-full text-lg text-gray-900 outline-none" data-id="element-3519" />
                        </fieldset>
                        <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3" data-id="element-3520">
                          <legend className="text-sm text-gray-500 px-1" data-id="element-3521">
                            Reasoning
                          </legend>
                          <textarea rows={4} className="w-full text-lg text-gray-900 outline-none resize-none" data-id="element-3522" />
                        </fieldset>
                      </div>
                      <div className="flex justify-end gap-4 mt-8" data-id="element-3523">
                        <button onClick={() => setActiveModal(null)} className="text-indigo-600 font-semibold px-6 py-3 hover:bg-indigo-50 rounded" data-id="element-3524">
                          CANCEL
                        </button>
                        <button onClick={() => handleDecision('approvedWithChanges')} className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-8 py-3 rounded" data-id="element-3525">
                          CONFIRM APPROVAL
                        </button>
                      </div>
                    </>}

                  {activeModal === 'reject' && <>
                      <h2 className="text-3xl font-bold text-gray-900 mb-8" data-id="element-3526">
                        Reject
                      </h2>
                      <div className="space-y-6" data-id="element-3527">
                        <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3" data-id="element-3528">
                          <legend className="text-sm text-gray-500 px-1" data-id="element-3529">
                            Reasoning
                          </legend>
                          <textarea rows={6} className="w-full text-lg text-gray-900 outline-none resize-none" data-id="element-3530" />
                        </fieldset>
                      </div>
                      <div className="flex justify-end gap-4 mt-8" data-id="element-3531">
                        <button onClick={() => setActiveModal(null)} className="text-indigo-600 font-semibold px-6 py-3 hover:bg-indigo-50 rounded" data-id="element-3532">
                          CANCEL
                        </button>
                        <button onClick={() => handleDecision('rejected')} className="bg-red-600 hover:bg-red-700 text-white font-semibold px-8 py-3 rounded" data-id="element-3533">
                          CONFIRM REJECTION
                        </button>
                      </div>
                    </>}
                </div>
              </motion.div>
            </>}
        </AnimatePresence>

        {activeTab === 'OVERVIEW' ? <WonkaOverviewTab data-id="element-3534" /> : activeTab === 'FINANCIALS' ? <WonkaFinancialsTab data-id="element-3535" /> : activeTab === 'PEOPLE' ? <WonkaPeopleTab data-id="element-3536" /> : activeTab === 'DETAILS' ? <WonkaDetailsTab data-id="element-3537" /> : activeTab === 'NOTIFICATIONS' ? <WonkaNotificationsTab data-id="element-3538" /> : activeTab === 'RESEARCH AGENTS' ? <WonkaResearchAgentsTab data-id="element-3539" /> : activeTab === 'APPLICATIONS' ? <WonkaApplicationView data-id="element-3540" /> : activeTab === 'AUDIT LOG' ? <WonkaAuditLogTab data-id="element-3541" /> : <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-12 text-center text-gray-500" data-id="element-3542">
            Content for {activeTab} tab coming soon.
          </div>}
      </div>

      <NotesModal isOpen={showNotesModal} onClose={() => setShowNotesModal(false)} notes={wonkaNotes} companyName="WONKA INDUSTRIES" data-id="element-3543" />

      <ScoutAIButton companyName="wonka" data-id="element-3544" />
    </div>;
}